<template>
  <div
    class="qr-scanner-wrapper"
    :class="{ 'qr-scanner-wrapper--fill': fillContainer }"
  >
    <camera-permission
      v-if="errorName"
      :error-name="errorName"
      @retry="retryCamera"
    />
    <video
      v-show="!errorName"
      ref="video"
      class="qr-scanner-video"
      autoplay
      muted
      playsinline
    ></video>
  </div>
</template>

<script>
import { BrowserCodeReader, BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat } from '@zxing/library';
import CameraPermission from './CameraPermission.vue';
import * as Sentry from '@sentry/vue';
import { getCameras, pickDefaultCameraId } from '@/utils/cameraDevices';

export default {
  components: { CameraPermission },
  props: {
    cameraId: {
      type: String,
      default: null
    },
    fillContainer: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      scanner: null,
      scannerControls: null,
      cameras: [],
      selectedCamera: null,
      result: null,
      errorName: null
    };
  },

  watch: {
    cameraId(newId) {
      if (newId && typeof newId === 'string') {
        this.selectedCamera = newId;
        if (this.scanner || this.cameras.length) this.restartScanner();
      }
    }
  },

  async mounted() {
    await this.loadCameras();
  },

  beforeDestroy() {
    this.stopScanner();
  },

  methods: {
    async loadCameras() {
      try {
        this.cameras = await getCameras();

        if (this.cameras.length) {
          if (this.cameraId) {
            this.selectedCamera = this.cameraId;
          } else {
            this.selectedCamera = pickDefaultCameraId(this.cameras);
          }

          await this.startScanner();
        } else {
          console.warn('No cameras found on this device.');
          this.errorName = 'NotFoundError';
        }
      } catch (error) {
        console.error(error);
        this.errorName = error.name || 'UnknownError';
        Sentry.captureException(error, {
          tags: { context: 'QrScanner.loadCameras', errorName: this.errorName }
        });
      }
    },

    async retryCamera() {
      this.errorName = null;
      await this.loadCameras();
    },

    async restartScanner() {
      if (this._restarting) return;
      this._restarting = true;
      try {
        await this.stopScanner();
        await this.startScanner();
      } finally {
        this._restarting = false;
      }
    },

    async startScanner() {
      await this.stopScanner();
      this.errorName = null;

      const scanner = new BrowserMultiFormatReader(undefined, {
        delayBetweenScanAttempts: 100,
        delayBetweenScanSuccess: 500
      });

      try {
        scanner.possibleFormats = [
          BarcodeFormat.QR_CODE,
          BarcodeFormat.CODE_128,
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E
        ];

        this.scanner = scanner;
        this.result = null;

        const controls = await scanner.decodeFromVideoDevice(
          this.selectedCamera || undefined,
          this.$refs.video,
          result => {
            if (this.scanner !== scanner || !result) return;

            const decodedText = result.getText();
            if (this.result === decodedText) return;

            this.result = decodedText;
            this.$emit('scan', decodedText);
          }
        );

        if (this.scanner !== scanner) {
          controls.stop();
          return;
        }

        this.scannerControls = controls;
      } catch (error) {
        if (this.scanner !== scanner) return;

        console.error('startScanner error:', error);
        this.errorName = error.name || 'UnknownError';
        Sentry.captureException(error, {
          tags: { context: 'QrScanner.startScanner', errorName: this.errorName },
          extra: { selectedCamera: this.selectedCamera }
        });
        this.scanner = null;
      }
    },

    async stopScanner() {
      try {
        if (this.scannerControls) this.scannerControls.stop();
        BrowserCodeReader.cleanVideoSource(this.$refs.video);
        this.scannerControls = null;
        this.scanner = null;
      } catch (error) {
        console.error('stopScanner error:', error);
        Sentry.captureException(error, { tags: { context: 'QrScanner.stopScanner' } });
        this.scannerControls = null;
        this.scanner = null;
      }
    }
  }
};
</script>

<style scoped>
.qr-scanner-wrapper {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.qr-scanner-video {
  width: 100% !important;
  height: 100% !important;
  display: block;
  object-fit: contain;
}
</style>
